import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./CountryInfo.css";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function CountryInfo({ isOpen, onClose, countryCode }) {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const slideContentRef = useRef(null);

  // Determine which country data to show
  const countryKey =
    countryCode === "ACC"
      ? "ghana"
      : countryCode === "CKY"
        ? "guinea"
        : countryCode === "ABJ"
          ? "ivoryCoast"
          : countryCode === "LOS"
            ? "nigeria"
            : countryCode === "DSS"
              ? "senegal"
              : countryCode === "BKO"
                ? "mali"
                : countryCode === "CMN"
                  ? "morocco"
                  : null;

  // Image URLs for each slide (using Unsplash)
  const getSlideImages = (country) => {
    if (country === "guinea") {
      return [
        "/images/nature.jpg", // Landscape/nature
        "/images/guinea/guinea_population.png", // People/community
        "https://images.unsplash.com/photo-1564760055775-d63b17a55c44?w=800&q=80", // Village/community
        "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800&q=80", // Culture/customs
        "/images/guinea/education.jpg", // School/education
        "/images/guinea/sport.png", // Sports/football
        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80", // Food
        "/images/guinea/eid.png", // Celebration/Eid
        "https://upload.wikimedia.org/wikipedia/en/thumb/5/5b/Mosqu%C3%A9e_Fay%C3%A7al_corrig%C3%A9e.jpg/960px-Mosqu%C3%A9e_Fay%C3%A7al_corrig%C3%A9e.jpg", // Faiçal Mosque, Conakry - aerial view (CC BY-SA 4.0)
        "/images/guinea/mining.png", // Mining/bauxite industry
        "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80", // Tourism/travel
      ];
    } else if (country === "senegal") {
      return [
        // Slide 1 - Landscape & Climate (sunset pirogues, Sine Saloum mangroves)
        "https://images.unsplash.com/photo-1583495506658-50a3587f792e?w=800&q=80", // 0: Sunset pirogues, Dakar (jBLsQqkEFuc)
        "https://images.unsplash.com/photo-1528046652967-217fde4a4529?w=800&q=80", // 1: Boat in mangroves, Sine Saloum Delta (LEpilI_K4fs)
        // Slide 2 - People & Demography (children eating, women at market)
        "https://images.unsplash.com/photo-1658129850537-ea7517a9682f?w=800&q=80", // 2: Children eating together, Mbour (AdX3zAKr_jI)
        "https://images.unsplash.com/photo-1625989744655-9bff7a23dac4?w=800&q=80", // 3: Women at market, Ziguinchor (vLZC-lONFg0)
        // Slide 3 - Communities (Gorée Island boats, Dakar harbor)
        "https://images.unsplash.com/photo-1648504735627-6af97e8337a9?w=800&q=80", // 4: Boats at Gorée Island, Dakar (7OVL_rjXSSM)
        "https://images.unsplash.com/photo-1615486905505-7863587b1c3f?w=800&q=80", // 5: Saint-Louis boats & buildings (−n1rIjE55Mw)
        // Slide 4 - Customs (Gorée street life, Touba Mosque — Sufi brotherhoods)
        "https://images.unsplash.com/photo-1603646681390-bb763559f730?w=800&q=80", // 6: Woman on Gorée Island street (QHyzaDIHqz0)
        "https://images.unsplash.com/photo-1664887248353-48fad226dbe6?w=800&q=80&fit=crop&crop=bottom", // 7: Great Mosque of Touba — Mouride/Tijaniyyah Sufi brotherhoods (MPmevL5ujsU)
        // Slide 5 - School & Work (Senegalese school children, Programme des Ecoles Digitales)
        "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f3/WikiChallenge_African_Schools_2022_in_S%C3%A9n%C3%A9gal_-_Visit_School_A_06.jpg/960px-WikiChallenge_African_Schools_2022_in_S%C3%A9n%C3%A9gal_-_Visit_School_A_06.jpg", // 8: School children in Senegal (CC BY-SA 4.0, Anthere)
        // AFCON 2021 celebration image
        "https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/C%C3%A9l%C3%A9bration_S%C3%A9n%C3%A9gal_%2812%29.jpg/960px-C%C3%A9l%C3%A9bration_S%C3%A9n%C3%A9gal_%2812%29.jpg", // 9a: AFCON 2021 celebration (CC BY-SA 4.0, Jeanpierrekepseu)
        // Slide 6 - Sport (Laamb wrestling at Demba Diop Stadium, Dakar)
        "https://upload.wikimedia.org/wikipedia/commons/thumb/1/13/SenegaleseWrestling2.JPG/960px-SenegaleseWrestling2.JPG", // 9b: Senegalese wrestling, Demba Diop Stadium (CC BY 3.0, KaBa)
        // Slide 7 - Food (Thieboudienne & Poulet Yassa — Senegal's iconic dishes)
        // "https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/Thieboudienne.JPG/960px-Thieboudienne.JPG", // 10a: Thieboudienne, Senegal's national dish (Public domain, KVDP)
        "/images/senegal/thiep.png",
        "https://upload.wikimedia.org/wikipedia/commons/thumb/d/dd/Poulet_yassa.jpg/960px-Poulet_yassa.jpg", // 10b: Poulet Yassa from Senegal (CC BY-SA 4.0, InspiFood)
        // Slide 8 - Celebrations (Grand Mosque of Touba — centerpiece of the Grand Magal pilgrimage)
        "https://upload.wikimedia.org/wikipedia/commons/0/0b/Touba_011_%2831692245327%29.jpg", // 11a: Grand Mosque of Touba exterior, site of the Grand Magal pilgrimage (CC BY 2.0, John Crane)
        // Slide 9 - Facts (African Renaissance Monument)
        "https://images.unsplash.com/photo-1681225241052-ac67808b0c62?w=800&q=80", // 12a: African Renaissance Monument, Dakar (KcUPQvvbr68)
        // Slide 10 - Tourism (Gorée Island, Faidherbe Bridge Saint-Louis, Djoudj pelicans)
        "https://images.unsplash.com/photo-1528047705243-ebb19baf436f?w=800&q=80", // 13a: Red houses alley, Gorée Island (CExAylTpX4w)
        "https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/Pont_faidherbe1.jpg/960px-Pont_faidherbe1.jpg", // 13b: Faidherbe Bridge, Saint-Louis (CC BY 2.0, Jeff Attaway)
        "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/Djoudj_ile_pelican.jpg/960px-Djoudj_ile_pelican.jpg", // 13c: Pelicans at Djoudj National Bird Sanctuary (CC BY-SA 4.0, Manu25)
      ];
    } else if (country === "ivoryCoast") {
      return [
        // Slide 1 - Landscape & Climate (coastal nature, tropical scenery)
        "https://images.unsplash.com/photo-1650285642154-f53a21bc6356?w=800&q=80", // 0: Rocky coast at Assouikro, Côte d'Ivoire (Giulio Eugeni)
        "https://images.unsplash.com/photo-1649589414700-4eef2b2d8779?w=800&q=80", // 1: Palm trees & ocean, Grand-Bassam, Côte d'Ivoire (Adams Banjo)
        // Slide 2 - People & Demography (population, youth, daily life)
        "https://images.unsplash.com/photo-1708347456805-b7f83316a8ba?w=800&q=80", // 2: Youth group smiling, Abidjan (Yanick Folly)
        "/images/ivory_coast/population.png",
        // "https://images.unsplash.com/photo-1734866660928-f7cd6e1b90f9?w=800&q=80", // 3: Treichville market people & fruits, Abidjan (Ingeborg Korme)
        // Slide 3 - Communities (city vs rural)
        "/images/ivory_coast/city.png", // 4: Abidjan Plateau skyline & Lagune Ébrié (djebi abraham philippe)
        "/images/ivory_coast/rural.png", // 5: Young girl carrying sack, rural Ivory Coast (Ben White)
        // Slide 4 - Customs (traditional clothing/pagne, greetings, hospitality)
        // "https://images.unsplash.com/photo-1552710307-537199cd41c0?w=800&q=80", // 6: Colorful textiles/pagne, Adjamé Market, Abidjan (Eva Blue)
        "/images/ivory_coast/customs.png",
        "https://images.unsplash.com/photo-1552709586-d1aefe5d04b4?w=800&q=80", // 7: People walking & greeting, Adjamé Market, Abidjan (Eva Blue)
        // Slide 5 - School & Work (agriculture, cocoa farming, commerce)
        "https://images.unsplash.com/photo-1507576164121-220762647800?w=800&q=80", // 8: Cocoa beans drying, Ivory Coast village (Etty Fidele)
        // Slide 6 - Sport (football, stadium)
        "https://images.unsplash.com/photo-1722978687695-212eecfa4cbe?w=800&q=80", // 9: Young men playing football, Africa (Dion Martins)
        // Slide 7 - Food (attiéké, grilled fish, alloco, kedjenou)
        "https://images.unsplash.com/photo-1718942899999-b3da4177ee2a?w=800&q=80", // 10: Grilled fish on banana leaf — attiéké with grilled fish is Ivory Coast's signature dish (Michael Lock)
        "https://images.unsplash.com/photo-1677094544034-01421fc6c874?w=800&q=80", // 11: Plate of grilled fish with vegetables — communal Ivorian meal style (Amo fif)
        // Slide 8 - Celebrations (festive street life, gatherings)
        "https://images.unsplash.com/photo-1769181613534-a4336671f8e1?w=800&q=80", // 12: Traditional African masks display, Fête des Masques (Eric Prouzet)
        // Slide 9 - Facts (Abidjan = "Paris of West Africa", modern skyline)
        "https://images.unsplash.com/photo-1507576164121-220762647800?w=800&q=80", // 13: Abidjan city buildings, Heden Golf Hotel area (djebi abraham philippe)
        // Slide 10 - Tourism (Abidjan city, sunset road, rural village life)
        "https://images.unsplash.com/photo-1552709607-b93cb1c5edec?w=800&q=80", // 14: Road at sunset, Abidjan (Eva Blue)
        "https://images.unsplash.com/photo-1690975719788-c0cf5b5692de?w=800&q=80", // 15: Woman carrying bucket, rural Ivory Coast (Ariel Nathan ADA MBITA)
        "https://images.unsplash.com/photo-1708347456816-db9d5d7efe3b?w=800&q=80", // 16: Man in red car, vibrant Abidjan street life (Yanick Folly)
      ];
    } else if (country === "ghana") {
      return [
        "/images/ghana/landscape1.png", // 0
        "/images/ghana/landscape2.png", // 1
        "/images/ghana/people1.png", // 2
        "/images/ghana/people2.png", // 3
        "/images/ghana/community1.png", // 4
        "/images/ghana/community2.png", // 5
        "/images/ghana/custom1.png", // 6
        "/images/ghana/custom2.png", // 7
        "/images/ghana/school1.png", // 8
        "/images/ghana/school2.png", // 9
        "/images/ghana/sport1.png", // 10
        "/images/ghana/sport2.png", // 11
        "/images/ghana/food1.png", // 12
        "/images/ghana/food2.png", // 13
        "/images/ghana/celebration1.png", // 14
        "/images/ghana/celebration2.png", // 15
        "/images/ghana/fact1.png", // 16
        "/images/ghana/fact2.png", // 17
        "/images/ghana/tourism1.png", // 18
        "/images/ghana/tourism2.png", // 19
      ];
    } else if (country === "nigeria") {
      return [
        // Slide 1 - Landscape
        "/images/nigeria/landscape1.png", // 0
        "/images/nigeria/landscape2.png", // 1
        // Slide 2 - People
        "/images/nigeria/people1.png", // 2
        "/images/nigeria/people2.png", // 3
        // Slide 3 - Communities
        "/images/nigeria/community1.png", // 4
        "/images/nigeria/community2.png", // 5
        // Slide 4 - Customs
        "/images/nigeria/customs1.png", // 6
        "/images/nigeria/customs2.png", // 7
        // Slide 5 - School
        "/images/nigeria/school1.png", // 8
        "/images/nigeria/school2.png", // 9
        // Slide 6 - Sport
        "/images/nigeria/sport1.png", // 10
        "/images/nigeria/sport2.png", // 11
        // Slide 7 - Food
        "/images/nigeria/food1.png", // 12
        "/images/nigeria/food2.png", // 13
        // Slide 8 - Celebrations
        "/images/nigeria/celebration1.png", // 14
        "/images/nigeria/celebration2.png", // 15
        // Slide 9 - Facts
        "/images/nigeria/fact1.png", // 16
        "/images/nigeria/fact2.png", // 17
        // Slide 10 - Tourism
        "/images/nigeria/tourism1.png", // 18
        "/images/nigeria/tourism2.png", // 19
      ];
    } else if (country === "mali") {
      return [
        "/images/mali/landscape1.png", // 0
        "/images/mali/landscape2.png", // 1
        "/images/mali/people1.png", // 2
        "/images/mali/people2.png", // 3
        "/images/mali/community1.png", // 4
        "/images/mali/community2.png", // 5
        "/images/mali/custom1.png", // 6
        "/images/mali/custom2.png", // 7
        "/images/mali/school1.png", // 8
        "/images/mali/school2.png", // 9
        "/images/mali/sport1.png", // 10
        "/images/mali/sport2.png", // 11
        "/images/mali/food1.png", // 12
        "/images/mali/food2.png", // 13
        "/images/mali/celebration1.png", // 14
        "/images/mali/celebration2.png", // 15
        "/images/mali/fact1.png", // 16
        "/images/mali/fact2.png", // 17
        "/images/mali/tourism1.png", // 18
        "/images/mali/tourism2.png", // 19
      ];
    } else if (country === "morocco") {
      return [
        // Slide 1 - Landscape
        "/images/morocco/lanscape1.png", // 0
        "/images/morocco/lanscape2.png", // 1
        // Slide 2 - People
        "/images/morocco/People1.png", // 2
        "/images/morocco/People2.png", // 3
        // Slide 3 - Communities
        "/images/morocco/community1.png", // 4
        "/images/morocco/community2.png", // 5
        // Slide 4 - Customs
        "/images/morocco/customs1.png", // 6
        "/images/morocco/customs2.png", // 7
        // Slide 5 - School
        "/images/morocco/school1.png", // 8
        "/images/morocco/school2.png", // 9
        // Slide 6 - Sport
        "/images/morocco/sport1.png", // 10
        "/images/morocco/sport2.png", // 11
        // Slide 7 - Food
        "/images/morocco/food1.png", // 12
        "/images/morocco/food2.png", // 13
        // Slide 8 - Celebrations
        "/images/morocco/celebration1.png", // 14
        "/images/morocco/celebration2.png", // 15
        // Slide 9 - Facts
        "/images/morocco/fact1.png", // 16
        "/images/morocco/fact2.png", // 17
        // Slide 10 - Tourism
        "/images/morocco/tourism1.png", // 18
        "/images/morocco/tourism2.png", // 19
      ];
    }
    return [];
  };

  const slideImages = countryKey ? getSlideImages(countryKey) : [];
  const hasImages = slideImages.length > 0;

  // Helper: return filtered image array or undefined (skips rendering)
  const imgs = (...indices) => {
    if (!hasImages) return undefined;
    const result = indices.map((i) => slideImages[i]).filter(Boolean);
    return result.length > 0 ? result : undefined;
  };
  const img = (index) => (hasImages ? slideImages[index] : undefined);

  const countryCoverImage = {
    ghana: "/images/ghana/intro.png",
    guinea: "/images/guinea/country.jpg",
    ivoryCoast: "/images/ivory_coast/intro.png",
    nigeria: "/images/nigeria/intro.png",
    senegal:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Senegal_Flag.jpg/960px-Senegal_Flag.jpg",
    mali: "/images/mali/intro.png",
    morocco: "/images/morocco/intro.png",
  };

  // Parse and structure slide content
  const parseSlideContent = (content) => {
    if (!content) return [];

    const lines = content.split("\n");
    const structured = [];
    let currentSection = null;

    lines.forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed) {
        // Empty line - end current section
        if (currentSection) {
          structured.push(currentSection);
          currentSection = null;
        }
        return;
      }

      // Check if it's a bullet point (• or -)
      if (trimmed.startsWith("•") || trimmed.startsWith("-")) {
        const text = trimmed.replace(/^[•-]\s*/, "");
        if (!currentSection || currentSection.type !== "list") {
          if (currentSection) structured.push(currentSection);
          currentSection = { type: "list", items: [text] };
        } else {
          currentSection.items.push(text);
        }
      }
      // Check if it's a subtitle (ends with colon)
      else if (trimmed.endsWith(":")) {
        if (currentSection) structured.push(currentSection);
        currentSection = { type: "subtitle", text: trimmed };
      }
      // Regular text
      else {
        if (!currentSection || currentSection.type !== "paragraph") {
          if (currentSection) structured.push(currentSection);
          currentSection = { type: "paragraph", text: trimmed };
        } else {
          currentSection.text += " " + trimmed;
        }
      }
    });

    if (currentSection) structured.push(currentSection);
    return structured;
  };

  const slides = countryKey
    ? [
        {
          title: t(`countryInfo.${countryKey}.title`),
          icon: null,
          content: t(`countryInfo.${countryKey}.summary`),
          coverImage: countryCoverImage[countryKey],
          isIntro: true,
        },
        {
          title: t(`countryInfo.${countryKey}.slide1.title`),
          icon: "🌍",
          content: t(`countryInfo.${countryKey}.slide1.content`),
          images:
            countryKey === "guinea"
              ? ["/images/guinea/nature.jpg", "/images/guinea/lanscape.png"]
              : countryKey === "ghana" ||
                  countryKey === "mali" ||
                  countryKey === "morocco" ||
                  countryKey === "nigeria"
                ? [img(0), img(1)]
                : imgs(0, 1),
        },
        {
          title: t(`countryInfo.${countryKey}.slide2.title`),
          icon: "📊",
          content: t(`countryInfo.${countryKey}.slide2.content`),
          images:
            countryKey === "guinea"
              ? [
                  "/images/guinea/guinea_population.png",
                  "/images/guinea/youth.png",
                ]
              : countryKey === "ghana" ||
                  countryKey === "mali" ||
                  countryKey === "morocco" ||
                  countryKey === "nigeria"
                ? [img(2), img(3)]
                : imgs(2, 3),
        },
        {
          title: t(`countryInfo.${countryKey}.slide3.title`),
          icon: "🏘️",
          content: t(`countryInfo.${countryKey}.slide3.content`),
          images:
            countryKey === "guinea"
              ? ["/images/guinea/city.png", "/images/guinea/rural.jpg"]
              : countryKey === "ghana" ||
                  countryKey === "mali" ||
                  countryKey === "morocco" ||
                  countryKey === "nigeria"
                ? [img(4), img(5)]
                : imgs(4, 5),
        },
        {
          title: t(`countryInfo.${countryKey}.slide4.title`),
          icon: "🤝",
          content: t(`countryInfo.${countryKey}.slide4.content`),
          images:
            countryKey === "guinea"
              ? ["/images/guinea/tradition.png", "/images/guinea/clothing.png"]
              : countryKey === "ivoryCoast"
                ? hasImages
                  ? [img(6), "/images/ivory_coast/market.png"]
                  : undefined
                : countryKey === "ghana" ||
                    countryKey === "mali" ||
                    countryKey === "morocco" ||
                    countryKey === "nigeria"
                  ? [img(6), img(7)]
                  : imgs(6),
        },
        {
          title: t(`countryInfo.${countryKey}.slide5.title`),
          icon: "📚",
          content: t(`countryInfo.${countryKey}.slide5.content`),
          image:
            countryKey === "guinea"
              ? "/images/guinea/education.jpg"
              : countryKey === "ghana" ||
                  countryKey === "ivoryCoast" ||
                  countryKey === "mali" ||
                  countryKey === "morocco" ||
                  countryKey === "nigeria"
                ? undefined
                : img(8),
          images:
            countryKey === "ghana" ||
            countryKey === "ivoryCoast" ||
            countryKey === "mali" ||
            countryKey === "morocco" ||
            countryKey === "nigeria"
              ? countryKey === "ivoryCoast"
                ? [
                    "/images/ivory_coast/school1.png",
                    "/images/ivory_coast/school2.png",
                  ]
                : [img(8), img(9)]
              : undefined,
          imageStyle:
            countryKey === "senegal"
              ? { objectPosition: "center 30%" }
              : undefined,
        },
        {
          title: t(`countryInfo.${countryKey}.slide6.title`),
          icon: "⚽",
          content: t(`countryInfo.${countryKey}.slide6.content`),
          image: countryKey === "guinea" ? img(5) : undefined,
          imagePortrait: countryKey === "guinea",
          images:
            countryKey === "ivoryCoast"
              ? [
                  "/images/ivory_coast/victory.png",
                  "/images/ivory_coast/team.png",
                ]
              : countryKey === "senegal"
                ? imgs(10, 9)
                : countryKey === "ghana" ||
                    countryKey === "mali" ||
                    countryKey === "morocco" ||
                    countryKey === "nigeria"
                  ? [img(10), img(11)]
                  : undefined,
        },
        {
          title: t(`countryInfo.${countryKey}.slide7.title`),
          icon: "🍚",
          content: t(`countryInfo.${countryKey}.slide7.content`),
          images:
            countryKey === "guinea"
              ? ["/images/guinea/food.png", "/images/guinea/recipe.png"]
              : countryKey === "ivoryCoast"
                ? [
                    "/images/ivory_coast/garba.png",
                    "/images/ivory_coast/attieke.png",
                    "/images/ivory_coast/fufu.png",
                  ]
                : countryKey === "ghana" ||
                    countryKey === "mali" ||
                    countryKey === "morocco" ||
                    countryKey === "nigeria"
                  ? [img(12), img(13)]
                  : imgs(11, 12),
        },
        {
          title: t(`countryInfo.${countryKey}.slide8.title`),
          icon: "🎉",
          content: t(`countryInfo.${countryKey}.slide8.content`),
          image:
            countryKey === "guinea"
              ? img(8)
              : countryKey === "ivoryCoast"
                ? "/images/ivory_coast/celebration.png"
                : countryKey === "ghana" ||
                    countryKey === "mali" ||
                    countryKey === "morocco" ||
                    countryKey === "nigeria"
                  ? undefined
                  : img(7),
          images:
            countryKey === "ghana" ||
            countryKey === "mali" ||
            countryKey === "morocco" ||
            countryKey === "nigeria"
              ? [img(14), img(15)]
              : undefined,
          imageLandscape: countryKey !== "morocco" && countryKey !== "nigeria",
        },
        {
          title: t(`countryInfo.${countryKey}.slide9.title`),
          icon: "⭐",
          content: t(`countryInfo.${countryKey}.slide9.content`),
          image:
            countryKey === "guinea"
              ? img(9)
              : countryKey === "ivoryCoast"
                ? img(8)
                : countryKey === "ghana" ||
                    countryKey === "mali" ||
                    countryKey === "morocco" ||
                    countryKey === "nigeria"
                  ? undefined
                  : img(14),
          images:
            countryKey === "ghana" ||
            countryKey === "mali" ||
            countryKey === "morocco" ||
            countryKey === "nigeria"
              ? [img(16), img(17)]
              : undefined,
          imageLandscape: countryKey === "senegal",
        },
        {
          title: t(`countryInfo.${countryKey}.slide10.title`),
          icon: "🧭",
          content: t(`countryInfo.${countryKey}.slide10.content`),
          images:
            countryKey === "guinea"
              ? [
                  "/images/guinea/cascade.jpg",
                  "/images/guinea/fouta.jpg",
                  "/images/guinea/island.png",
                ]
              : countryKey === "ivoryCoast"
                ? [
                    "/images/ivory_coast/assinie.png",
                    "/images/ivory_coast/abidjan.png",
                    img(16),
                  ]
                : countryKey === "ghana" ||
                    countryKey === "mali" ||
                    countryKey === "morocco" ||
                    countryKey === "nigeria"
                  ? [img(18), img(19)]
                  : imgs(15, 16, 17),
        },
      ]
    : [];

  const handleClose = useCallback(() => {
    setCurrentSlide(0);
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide, slides.length]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide]);

  // Keyboard navigation - MUST be before any conditional returns
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose, handleNext, handlePrev]);

  // Touch/Swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance for a swipe

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swiped left - go to next slide
        handleNext();
      } else {
        // Swiped right - go to previous slide
        handlePrev();
      }
    }

    // Reset values
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Helper to get WebP path from original image path (local images only)
  const getWebPPath = (src) => {
    if (!src || !src.startsWith("/images/")) return null;
    return src.replace(/\.(jpg|jpeg|png)$/i, ".webp");
  };

  // Render an image with WebP fallback using <picture>
  const renderImage = (src, alt, loading = "lazy", style) => {
    const webpSrc = getWebPPath(src);
    if (webpSrc) {
      return (
        <picture>
          <source srcSet={webpSrc} type="image/webp" />
          <img src={src} alt={alt} loading={loading} style={style} />
        </picture>
      );
    }
    return <img src={src} alt={alt} loading={loading} style={style} />;
  };

  // Early returns AFTER all hooks
  if (!isOpen || !countryCode) return null;
  if (!countryKey) return null;

  return (
    <div className="country-info-overlay" onClick={handleClose}>
      <div
        className="country-info-modal full-info"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="country-info-close" onClick={handleClose}>
          <CloseIcon />
        </button>

        <div
          className="country-info-full"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          ref={slideContentRef}
        >
          <div className="slide-header">
            {slides[currentSlide].icon && (
              <span className="slide-icon">{slides[currentSlide].icon}</span>
            )}
            <h2>
              {!slides[currentSlide].isIntro && (
                <span className="slide-country-name">
                  {t(`countryInfo.${countryKey}.title`)} —{" "}
                </span>
              )}
              {slides[currentSlide].title}
            </h2>
          </div>
          <div
            className={`slide-content ${currentSlide === 0 ? "first-slide" : ""}`}
          >
            {/* Intro slide cover image */}
            {slides[currentSlide].isIntro &&
              slides[currentSlide].coverImage && (
                <div className={`slide-image intro-cover cover-${countryKey}`}>
                  {renderImage(
                    slides[currentSlide].coverImage,
                    slides[currentSlide].title,
                    "eager",
                  )}
                </div>
              )}
            {/* Regular slide images */}
            {!slides[currentSlide].isIntro &&
              slides[currentSlide].images &&
              slides[currentSlide].images.length > 1 && (
                <div
                  className={`slide-image-grid${slides[currentSlide].images.length >= 3 ? " grid-3" : ""}`}
                >
                  {slides[currentSlide].images.map((src, i) => (
                    <div key={i} className="slide-image-grid-item">
                      {renderImage(
                        src,
                        `${slides[currentSlide].title} ${i + 1}`,
                      )}
                    </div>
                  ))}
                </div>
              )}
            {slides[currentSlide].image && (
              <div
                className={`slide-image${slides[currentSlide].imageLandscape ? " landscape" : slides[currentSlide].imagePortrait ? " portrait" : ""}`}
              >
                {renderImage(
                  slides[currentSlide].image,
                  slides[currentSlide].title,
                  "lazy",
                  slides[currentSlide].imageStyle,
                )}
              </div>
            )}
            {slides[currentSlide].images &&
              slides[currentSlide].images.length === 1 && (
                <div className="slide-image">
                  {renderImage(
                    slides[currentSlide].images[0],
                    slides[currentSlide].title,
                  )}
                </div>
              )}
            <div
              className={`slide-text${slides[currentSlide].isIntro ? " intro-text" : ""}`}
            >
              {parseSlideContent(slides[currentSlide].content).map(
                (section, idx) => {
                  if (section.type === "subtitle") {
                    return (
                      <h3 key={idx} className="slide-subtitle">
                        {section.text}
                      </h3>
                    );
                  } else if (section.type === "list") {
                    return (
                      <ul key={idx} className="slide-list">
                        {section.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    );
                  } else {
                    return (
                      <p key={idx} className="slide-paragraph">
                        {section.text}
                      </p>
                    );
                  }
                },
              )}
            </div>
          </div>
          {/* Swipe indicator for mobile - only show on first slide */}
          {currentSlide === 0 && (
            <div className="swipe-indicator">
              <div className="swipe-hint">
                <ArrowBackIcon className="swipe-arrow swipe-arrow-left" />
                <span className="swipe-text">{t("countryInfo.swipeHint")}</span>
                <ArrowForwardIcon className="swipe-arrow swipe-arrow-right" />
              </div>
            </div>
          )}
          <div className="slide-navigation">
            <button
              className="nav-button"
              onClick={handlePrev}
              disabled={currentSlide === 0}
            >
              <ArrowBackIcon />
            </button>
            <span className="slide-counter">
              {currentSlide + 1} / {slides.length}
            </span>
            <button
              className="nav-button"
              onClick={handleNext}
              disabled={currentSlide === slides.length - 1}
            >
              <ArrowForwardIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CountryInfo;
